import { fetchPromotionsWithWelcomeBonus } from '@/services/promotions'
import { useUserStore } from '@/stores/user'
import type {
  PromotionBanner,
  PromotionProgressEntry,
  WelcomeBonus,
} from '@/types'
import { type Ref, computed, ref } from 'vue'

type LoadResult = { success: boolean; error: any }

const welcomeBonus = ref<WelcomeBonus | null>(null) as Ref<WelcomeBonus | null>
const promotions = ref<PromotionBanner[]>([])
const promotionProgress = ref<PromotionProgressEntry[]>([])
const isLoading = ref(false)
const error = ref<Error | null>(null)

let hasLoadedSuccessfully = false
let lastLoadResult: LoadResult = { success: false, error: null }
let inflightLoad: Promise<LoadResult> | null = null
const WELCOME_PROMO_CODE = 'welcome-deposit-match'

/**
 * Find the welcome bonus entry from promotion_progress array
 * Only returns a value when there's an actual grant (pending_wagering, fulfilled, revoked).
 * Returns null for pending_reservation so the banner can display the upsell.
 * Maps backend status strings to UI-expected values.
 */
function findWelcomeProgress(
  entries: PromotionProgressEntry[]
): WelcomeBonus | null {
  const entry = entries.find((e) => e.code === WELCOME_PROMO_CODE)
  if (!entry) {
    return null
  }

  // Map backend statuses to UI statuses
  const statusMap: Record<
    PromotionProgressEntry['status'],
    WelcomeBonus['status']
  > = {
    pending_reservation: 'pending_reservation',
    active: 'pending_wagering',
    fulfilled: 'fulfilled',
    revoked: 'revoked',
  }

  const normalizedStatus = statusMap[entry.status] ?? 'pending_reservation'

  // Return null for pending_reservation so the banner shows
  if (normalizedStatus === 'pending_reservation') {
    return null
  }

  // Map PromotionProgressEntry to WelcomeBonus format with normalized status
  return {
    status: normalizedStatus,
    expires_at: entry.expires_at,
    progress: entry.progress,
    details: entry.details,
  }
}

async function fetchAndPopulate(): Promise<LoadResult> {
  try {
    const { data, error: fetchError } = await fetchPromotionsWithWelcomeBonus()

    if (fetchError.value) {
      error.value = fetchError.value as Error
      console.error(
        '[usePromotions] Failed to fetch promotions:',
        fetchError.value
      )
      hasLoadedSuccessfully = false
      lastLoadResult = { success: false, error: fetchError.value }
      return lastLoadResult
    }

    if (data.value) {
      promotions.value = data.value.promotions || []
      const progressEntries = data.value.promotion_progress || []
      promotionProgress.value = progressEntries
      welcomeBonus.value = findWelcomeProgress(progressEntries)
    }

    hasLoadedSuccessfully = true
    lastLoadResult = { success: true, error: null }
    return lastLoadResult
  } catch (e) {
    error.value = e as Error
    console.error('[usePromotions] Exception fetching promotions:', e)
    hasLoadedSuccessfully = false
    lastLoadResult = { success: false, error: e }
    return lastLoadResult
  }
}

/**
 * Composable for managing promotions and welcome bonus data.
 * Call loadPromotions() to fetch the latest data from the API.
 */
const welcomePromotion = computed(() =>
  promotions.value.find((promo) => promo.code === WELCOME_PROMO_CODE)
)

export function usePromotions() {
  /**
   * Fetch promotions and welcome bonus data from the API
   * @param options.force - If true, forces a fresh fetch even if data was previously loaded
   */
  async function loadPromotions(options?: { force?: boolean }) {
    const force = options?.force ?? false

    // Return cached result if not forcing and we have successful data
    if (
      (!force &&
        hasLoadedSuccessfully &&
        (promotions.value.length > 0 || promotionProgress.value.length > 0) &&
        !inflightLoad) ||
      useUserStore().isLoggedOut
    ) {
      return lastLoadResult
    }

    // Reuse in-flight request if one exists
    if (!inflightLoad) {
      inflightLoad = (async () => {
        isLoading.value = true
        error.value = null

        const result = await fetchAndPopulate()

        isLoading.value = false
        inflightLoad = null
        return result
      })()
    }

    return inflightLoad
  }

  /**
   * Get the first active or pending_reservation promotion for banner display
   */
  function getActivePromotionBanner(): PromotionBanner | null {
    return (
      promotions.value.find(
        (promo) =>
          promo.status === 'active' || promo.status === 'pending_reservation'
      ) ?? null
    )
  }

  /**
   * Helper to determine if welcome bonus progress should be shown
   * (excludes pending_reservation state where upsell banner is more appropriate)
   */
  const hasWelcomeProgress = computed(
    () =>
      !!welcomeBonus.value &&
      welcomeBonus.value.status !== 'pending_reservation'
  )

  /**
   * Check if the welcome bonus is currently eligible for display
   * Returns true when the promotion exists and user hasn't claimed it yet
   */
  const showWelcomeBanner = computed(() => {
    if (welcomeBonus.value) {
      return false
    }

    const promo = welcomePromotion.value
    if (!promo || promo.status !== 'pending_reservation') {
      return false
    }

    const progressEntry = promotionProgress.value.find(
      (e) => e.code === WELCOME_PROMO_CODE
    )

    return !progressEntry || progressEntry.status === 'pending_reservation'
  })

  return {
    welcomeBonus,
    promotions,
    promotionProgress,
    isLoading,
    error,
    loadPromotions,
    getActivePromotionBanner,
    hasWelcomeProgress,
    showWelcomeBanner,
    welcomePromotion,
  }
}
